import { useAuthenticator } from "@aws-amplify/ui-react";
import { useStore } from "@nanostores/react";
import { redirectPage } from "@nanostores/router";
import * as Sentry from "@sentry/react";
import { fetchAuthSession } from "aws-amplify/auth";
import { lazy, ReactElement, Suspense, useEffect } from "react";

import { client } from "../api/client.gen.ts";
import { WorkflowStatus } from "../api/types.gen.ts";
import { StackedLayout } from "../components/catalyst/StackedLayout.tsx";
import { GraphLayout } from "../components/GraphLayout.tsx";
import { LoadingIndicator } from "../components/LoadingIndicator.tsx";
import { RailLayout } from "../components/RailLayout.tsx";
import { getAppConfig } from "../config/get-app-config.ts";
import { withDataLoaded } from "../context/withDataLoaded.tsx";
import { $agentContent } from "../stores/agent.ts";
import { $destinationContent } from "../stores/destinations.ts";
import { $entityContent } from "../stores/entities.ts";
import { $entityClass } from "../stores/entity-classes.ts";
import { $eventContent } from "../stores/events.ts";
import { $extensionInfoBlockContent } from "../stores/extension-infoblocks.ts";
import { $extensionPolicyContent } from "../stores/extension-policies.ts";
import { $integrationContent } from "../stores/integrations.ts";
import { $knowledgeBaseContent } from "../stores/knowledge-base.ts";
import { $pollerContent } from "../stores/pollers.ts";
import { $queryActionContent } from "../stores/queryactions.ts";
import { $router } from "../stores/router.ts";
import { AuthCallback } from "./AuthCallback.tsx";
import EntityEdit from "./EntityEdit.tsx";
import { PageNotFound } from "./PageNotFound.tsx";
import { QueryActionEdit } from "./QueryActionEdit.tsx";
import SignIn from "./SignIn.tsx";

const HomePage = lazy(() => import("./Home.tsx"));
const IdentitiesPage = lazy(() => import("./Identities.tsx"));
const IdentityDetailPage = lazy(() => import("./IdentityDetail.tsx"));
const IntegrationsList = lazy(() => import("./IntegrationsList.tsx"));
const IntegrationType = lazy(() => import("./IntegrationType.tsx"));
const SettingsPage = lazy(() => import("./Settings.tsx"));
const Graph = lazy(() => import("./Graph.tsx"));
const IntegrationForm = lazy(() => import("./IntegrationForm.tsx"));
const EntitiesPage = lazy(() => import("./Entities.tsx"));
const EntityClassFormPage = lazy(() => import("./EntityClassFormPage.tsx"));
const EntityFormFullPage = lazy(() => import("./EntityFormFullPage.tsx"));
const QueryActionForm = lazy(() => import("./QueryActionForm.tsx"));
const Dashboard = lazy(() => import("./Dashboard.tsx"));
const Alerts = lazy(() => import("./Alerts.tsx"));
const AlertDetails = lazy(() => import("./AlertDetails.tsx"));
const AgentForm = lazy(() => import("./AgentForm.tsx"));
const AgentsList = lazy(() => import("./AgentList.tsx"));
const KnowledgeBase = lazy(() => import("./KnowledgeBase.tsx"));
const KnowledgeBaseDetail = lazy(() => import("./KnowledgeBaseDetail.tsx"));
const Destinations = lazy(() => import("./Destinations.tsx"));
const DestinationType = lazy(() => import("./DestinationType.tsx"));
const DestinationForm = lazy(() => import("./DestinationForm.tsx"));
const Chat = lazy(() => import("./Chat.tsx"));
const InvokeAgent = lazy(() => import("./InvokeAgent.tsx"));
const PollersList = lazy(() => import("./PollersList.tsx"));
const PollerForm = lazy(() => import("./PollerForm.tsx"));
const ExtensionPoliciesList = lazy(() => import("./ExtensionPoliciesList.tsx"));
const ExtensionPolicyForm = lazy(() => import("./ExtensionPolicyForm.tsx"));
const ExtensionPolicyType = lazy(() => import("./ExtensionPolicyType.tsx"));
const ExtensionInfoBlockForm = lazy(
  () => import("./ExtensionInfoBlockForm.tsx"),
);

// Loader wrappers for HOC-safe usage
const IntegrationEditForm = (): ReactElement => {
  const Wrapped = withDataLoaded($integrationContent, IntegrationForm, {
    propKey: "integration",
  });
  return <Wrapped />;
};

const QueryActionNewForm = (): ReactElement => {
  //@ts-expect-error - QueryActionForm expects a narrow integrations but we are passing a wider integration
  const WithIntegration = withDataLoaded($integrationContent, QueryActionForm, {
    propKey: "integration",
  });
  return <WithIntegration />;
};

const QueryActionEditForm = (): ReactElement => {
  const WithQueryAction = withDataLoaded($queryActionContent, QueryActionEdit, {
    propKey: "queryAction",
  });
  return <WithQueryAction />;
};

const EntityClassEditForm = (): ReactElement => {
  const Wrapped = withDataLoaded($entityClass, EntityClassFormPage, {
    propKey: "entityClass",
  });
  return <Wrapped />;
};

const AgentEdit = (): ReactElement => {
  const Wrapped = withDataLoaded($agentContent, AgentForm, {
    propKey: "agent",
  });
  return <Wrapped />;
};

const InvokeAgentFormWithData = (): ReactElement => {
  const Wrapped = withDataLoaded($agentContent, InvokeAgent, {
    propKey: "agent",
  });
  return <Wrapped />;
};

const DestinationEditForm = (): ReactElement => {
  const Wrapped = withDataLoaded($destinationContent, DestinationForm, {
    propKey: "destination",
  });
  return <Wrapped />;
};

const ExtensionPolicyEditForm = (): ReactElement => {
  const Wrapped = withDataLoaded($extensionPolicyContent, ExtensionPolicyForm, {
    propKey: "policy",
  });
  return <Wrapped />;
};

const ExtensionInfoBlockEditForm = (): ReactElement => {
  const Wrapped = withDataLoaded(
    $extensionInfoBlockContent,
    ExtensionInfoBlockForm,
    {
      propKey: "infoBlock",
    },
  );
  return <Wrapped />;
};

const EntityNewForm = (): ReactElement => {
  //@ts-expect-error - EntityFormFullPage expects a narrow entityClass but we are passing a wider entityClass
  const Wrapped = withDataLoaded($entityClass, EntityFormFullPage, {
    propKey: "entityClass",
  });
  return <Wrapped />;
};

const EntityEditForm = (): ReactElement => {
  const WithEntity = withDataLoaded($entityContent, EntityEdit, {
    propKey: "entity",
  });
  return <WithEntity />;
};

const PollerEditForm = (): ReactElement => {
  const Wrapped = withDataLoaded($pollerContent, PollerForm, {
    propKey: "poller",
  });
  return <Wrapped />;
};

const AlertDetailsFormWithData = (): ReactElement => {
  const Wrapped = withDataLoaded($eventContent, AlertDetails, {
    propKey: "event",
    refreshInterval: 10000, // Poll every 10 seconds
    shouldStopPolling: (data) => {
      const terminalStatuses: WorkflowStatus[] = [
        WorkflowStatus.ABORTED,
        WorkflowStatus.FAILED,
        WorkflowStatus.SUCCEEDED,
        WorkflowStatus.TIMED_OUT,
      ];
      return terminalStatuses.includes(data.status);
    },
  });
  return <Wrapped />;
};

const KnowledgeBaseDetailWithData = (): ReactElement => {
  const Wrapped = withDataLoaded($knowledgeBaseContent, KnowledgeBaseDetail, {
    propKey: "knowledgeBase",
  });
  return <Wrapped reload={() => $knowledgeBaseContent.fetch()} />;
};

const appConfig = getAppConfig();

export const Routes = (): null | ReactElement => {
  const page = useStore($router);
  const auth = useAuthenticator((context) => [context.authStatus]);

  useEffect(() => {
    const setupAuth = async (): Promise<void> => {
      if (auth.authStatus === "unauthenticated") {
        redirectPage($router, "signIn");
      }

      try {
        const authSession = await fetchAuthSession();
        const rawToken = authSession.tokens?.accessToken?.toString();

        if (rawToken) {
          client.setConfig({
            auth: rawToken,
            baseUrl: appConfig.backendUrl,
            throwOnError: true,
          });

          if (appConfig.envMode === "production") {
            client.interceptors.response.use(async (response) => {
              if (!response.ok) {
                // Clone response to preserve original body stream which can only be consumed once
                const clonedResponse = response.clone();
                const body = await clonedResponse.json();

                Sentry.captureException(
                  new Error(
                    `API Error: ${response.status} ${response.statusText}`,
                  ),
                  {
                    extra: {
                      status: response.status,
                      statusText: response.statusText,
                      url: response.url,
                      ...(body as Record<string, unknown>),
                    },
                    tags: {
                      source: "hey-api-client",
                    },
                  },
                );
              }

              return response;
            });
          }
        }
      } catch (error) {
        console.error("Error setting up authentication:", error);
      }
    };

    void setupAuth();
  }, [auth.authStatus]);

  if (page?.route === "signIn") {
    if (auth.authStatus === "authenticated") {
      redirectPage($router, "dashboard");
    }

    if (auth.authStatus === "unauthenticated") {
      return <SignIn />;
    }
  }

  if (page?.route === "authCallback") {
    return (
      <StackedLayout navbar={null} sidebar={null}>
        <AuthCallback />
      </StackedLayout>
    );
  }

  if (auth.authStatus === "configuring") {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <LoadingIndicator />
      </div>
    );
  }

  if (page === undefined) {
    return (
      <RailLayout>
        <PageNotFound />
      </RailLayout>
    );
  }

  if (page.route === "root") {
    if (auth.authStatus === "unauthenticated") {
      redirectPage($router, "signIn");
    } else if (auth.authStatus === "authenticated") {
      redirectPage($router, "dashboard");
    }
    return null;
  }

  if (page.route === "index") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <HomePage />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "chat") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <Chat />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "dashboard") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <Dashboard />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "identities") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <IdentitiesPage />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "identitiesDetail") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <IdentityDetailPage />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "integrations") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <IntegrationsList />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "integrationsCreate") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <IntegrationType />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "settings") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <SettingsPage />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "graph") {
    return (
      <GraphLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <Graph />
        </Suspense>
      </GraphLayout>
    );
  }

  if (page.route === "newIntegrations") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <IntegrationForm integrationType={page.params.type} />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "integrationsEdit") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <IntegrationEditForm />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "entities") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <EntitiesPage />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "entityClassCreate") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <EntityClassFormPage />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "entityClassEdit") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <EntityClassEditForm />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "entityCreate") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <EntityNewForm />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "entityEdit") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <EntityEditForm />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "queryActionNew" || page.route === "queryActionEdit") {
    if (page.route === "queryActionNew") {
      return (
        <RailLayout>
          <Suspense fallback={<LoadingIndicator />}>
            <QueryActionNewForm />
          </Suspense>
        </RailLayout>
      );
    }

    if (page.route === "queryActionEdit") {
      return (
        <RailLayout>
          <Suspense fallback={<LoadingIndicator />}>
            <QueryActionEditForm />
          </Suspense>
        </RailLayout>
      );
    }
  }

  if (page.route === "alerts") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <Alerts />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "alertDetails") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <AlertDetailsFormWithData />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "agents") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <AgentsList />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "agentsCreate") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <AgentForm />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "agentsEdit") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <AgentEdit />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "pollers") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <PollersList />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "pollersCreate") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <PollerForm />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "pollersEdit") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <PollerEditForm />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "knowledgeBase") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <KnowledgeBase />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "knowledgeBaseDetail") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <KnowledgeBaseDetailWithData />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "destinations") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <Destinations />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "destinationsCreate") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <DestinationType />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "newDestinations") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <DestinationForm destinationType={page.params.type} />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "destinationsEdit") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <DestinationEditForm />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "agentInvoke") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <InvokeAgentFormWithData />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "extensionPolicies") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <ExtensionPoliciesList />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "extensionInfoBlocksCreate") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <ExtensionInfoBlockForm />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "extensionInfoBlocksEdit") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <ExtensionInfoBlockEditForm />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "extensionPoliciesCreate") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <ExtensionPolicyType />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "newExtensionPolicyUrlAccess") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <ExtensionPolicyForm />
        </Suspense>
      </RailLayout>
    );
  }

  if (page.route === "extensionPoliciesEdit") {
    return (
      <RailLayout>
        <Suspense fallback={<LoadingIndicator />}>
          <ExtensionPolicyEditForm />
        </Suspense>
      </RailLayout>
    );
  }

  return null;
};
